import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const SOUND_KEY = 'lead-tracker-notification-sound-v1';

function playTone() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return;
  const ctx = new AudioCtx();
  const now = ctx.currentTime;
  [[440, 0], [330, 0.13], [440, 0.26]].forEach(([frequency, offset]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, now + offset);
    gain.gain.exponentialRampToValueAtTime(0.08, now + offset + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + offset);
    osc.stop(now + offset + 0.11);
  });
  window.setTimeout(() => ctx.close().catch(() => {}), 700);
}

function when(value) {
  if (!value) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

const attentionHref = (item) => item?.attention_item_id ? `/attention?item=${encodeURIComponent(item.attention_item_id)}` : '/attention';

export default function NotificationListener({ compact = false, readOnly = false }) {
  const { workspace } = useAuth();
  const [enabled, setEnabled] = useState(() => localStorage.getItem(SOUND_KEY) !== 'off');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const lastSeenRef = useRef(null);
  const readyRef = useRef(false);
  const centerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const unlock = () => { readyRef.current = true; };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!centerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onScroll = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const load = useCallback(async ({ announce = true } = {}) => {
    if (!workspace?.id) return;
    const { data, error } = await supabase
      .from('notifications')
      .select('id,kind,title,body,read_at,created_at,attention_item_id,lead_id')
      .eq('workspace_id', workspace.id)
      .eq('kind', 'needs_attention')
      .order('created_at', { ascending: false })
      .limit(15);
    if (error) return;
    const next = data || [];
    setItems(next);
    const latest = next[0];
    if (!latest) {
      lastSeenRef.current = null;
      return;
    }
    if (lastSeenRef.current === null) {
      lastSeenRef.current = latest.id;
      return;
    }
    if (announce && latest.id !== lastSeenRef.current) {
      lastSeenRef.current = latest.id;
      setToast(latest);
      if (enabled && readyRef.current) playTone();
      window.setTimeout(() => setToast((current) => current?.id === latest.id ? null : current), 6500);
    }
  }, [workspace?.id, enabled]);

  useEffect(() => {
    if (!workspace?.id) return;
    lastSeenRef.current = null;
    load({ announce: false });
    const timer = window.setInterval(() => load({ announce: true }), 5000);
    return () => window.clearInterval(timer);
  }, [workspace?.id, load]);

  const unread = readOnly ? 0 : items.filter(item => !item.read_at).length;

  async function markRead(id) {
    if (readOnly) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('workspace_id', workspace.id)
      .eq('id', id)
      .eq('kind', 'needs_attention');
    if (!error) setItems(current => current.map(item => item.id === id ? { ...item, read_at: new Date().toISOString() } : item));
  }

  async function markAllRead() {
    if (readOnly) return;
    const ids = items.filter(item => !item.read_at).map(item => item.id);
    if (!ids.length) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('workspace_id', workspace.id)
      .eq('kind', 'needs_attention')
      .is('read_at', null);
    if (!error) setItems(current => current.map(item => ({ ...item, read_at: item.read_at || now })));
  }

  function toggleSound() {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(SOUND_KEY, next ? 'on' : 'off');
    if (next && readyRef.current) playTone();
  }

  return <div className={`notification-center${compact ? ' compact' : ''}`} ref={centerRef}>
    <button className="notification-bell" type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-label={`Needs Attention notifications${unread ? `, ${unread} unread` : ''}`}>
      {compact ? <svg className="notification-bell-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg> : <span className="notification-bell-icon" aria-hidden="true">●</span>}
      {!compact && <span>Notifications</span>}
      {unread > 0 && <span className="notification-badge">{unread > 99 ? '99+' : unread}</span>}
    </button>

    {open && <div className="notification-popover" ref={popoverRef}>
      <div className="notification-popover-head">
        <div><strong>Needs Attention</strong><span>{readOnly ? 'Notifications' : unread ? `${unread} unread` : 'All caught up'}</span></div>
        <div className="notification-popover-head-actions">
          {!readOnly && unread > 0 && <button type="button" onClick={markAllRead}>Mark all read</button>}
          <button type="button" className="notification-popover-close" onClick={() => setOpen(false)} aria-label="Close notifications">×</button>
        </div>
      </div>
      <div className="notification-list">
        {items.length === 0 ? <div className="notification-empty">No Needs Attention notifications.</div> : items.map(item => <Link
          key={item.id}
          to={attentionHref(item)}
          className={`notification-item${!readOnly && !item.read_at ? ' unread' : ''}`}
          onClick={() => { if (!readOnly) markRead(item.id); setOpen(false); }}
        >
          <span className="notification-item-dot" aria-hidden="true" />
          <span className="notification-item-copy"><strong>{item.title}</strong>{item.body && <small>{item.body}</small>}</span>
          <time>{when(item.created_at)}</time>
        </Link>)}
      </div>
      <div className="notification-popover-foot">
        <Link to="/attention" onClick={() => setOpen(false)}>Open Needs Attention</Link>
        <button type="button" onClick={toggleSound}>{enabled ? 'Sound on' : 'Sound off'}</button>
      </div>
    </div>}

    {toast && <div className="notification-toast attention" role="status">
      <div><strong>{toast.title}</strong>{toast.body && <span>{toast.body}</span>}<Link to={attentionHref(toast)} onClick={() => { if (!readOnly) markRead(toast.id); setToast(null); }}>Open Needs Attention →</Link></div>
      <button aria-label="Dismiss notification" onClick={() => setToast(null)}>×</button>
    </div>}
  </div>;
}
